import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const LoginAlternative = () => {
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleLoaded, setGoogleLoaded] = useState(false);
  const googleButtonRef = useRef(null);

  useEffect(() => {
    initializeGoogleSignIn();
  }, []);

  const initializeGoogleSignIn = () => {
    setError('');
    if (window.google) {
      setupGoogleSignIn();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
    script.onload = () => setupGoogleSignIn();
    script.onerror = () => setError('Failed to load Google Sign-In. Check your connection.');
  };

  const setupGoogleSignIn = () => {
    if (window.google) {
      try {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: handleGoogleResponse,
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        if (googleButtonRef.current) {
          googleButtonRef.current.innerHTML = '';
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            theme: 'filled_black',
            size: 'large',
            width: 300,
            text: 'continue_with',
            shape: 'pill',
          });
        }
        setGoogleLoaded(true);
      } catch (err) {
        setError('Failed to initialize Google Sign-In.');
      }
    }
  };

  const handleGoogleResponse = async (response) => {
    setLoading(true);
    setError('');
    try {
      const result = await login(response.credential);
      if (!result.success) {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setError('');
    setGoogleLoaded(false);
    initializeGoogleSignIn();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#0d1117',
        color: '#e6edf3',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background gradient effects */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `
            radial-gradient(ellipse 80% 60% at 50% -20%, rgba(88,166,255,0.12) 0%, transparent 60%),
            radial-gradient(ellipse 60% 50% at 80% 50%, rgba(63,185,80,0.06) 0%, transparent 50%),
            radial-gradient(ellipse 60% 50% at 20% 80%, rgba(88,166,255,0.05) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        }}
      />

      {/* Top nav */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 3, md: 5 },
          py: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, #58a6ff, #3fb950)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: 15,
              color: '#fff',
              boxShadow: '0 4px 16px rgba(88,166,255,0.3)',
            }}
          >
            SA
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Simple Analysis</Typography>
        </Box>
      </Box>

      {/* Hero */}
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 4,
        }}
      >
        <Box sx={{ maxWidth: 520, textAlign: 'center' }}>
          {/* Badge */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.8,
              px: 1.8,
              py: 0.6,
              mb: 3.5,
              borderRadius: 5,
              border: '1px solid rgba(88,166,255,0.2)',
              backgroundColor: 'rgba(88,166,255,0.08)',
              fontSize: 12,
              fontWeight: 600,
              color: '#58a6ff',
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: '#3fb950',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.4 },
                },
              }}
            />
            AI-Powered Trading Platform
          </Box>

          {/* Headline */}
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3rem' },
              lineHeight: 1.15,
              letterSpacing: '-1.5px',
              mb: 2,
              background: 'linear-gradient(135deg, #e6edf3 0%, #8b949e 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Smarter trades with{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(135deg, #58a6ff 0%, #3fb950 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              AI-driven signals
            </Box>
          </Typography>

          <Typography sx={{ fontSize: 17, color: '#8b949e', lineHeight: 1.6, mb: 5, maxWidth: 440, mx: 'auto' }}>
            Technical analysis, real-time news, and AI recommendations — all working together to find your next trade.
          </Typography>

          {/* Login Card */}
          <Box
            sx={{
              backgroundColor: 'rgba(22,27,34,0.8)',
              border: '1px solid #21262d',
              borderRadius: 4,
              p: 4,
              backdropFilter: 'blur(12px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
              maxWidth: 380,
              mx: 'auto',
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 0.5 }}>Welcome back</Typography>
            <Typography sx={{ fontSize: 13, color: '#8b949e', mb: 3 }}>
              Sign in to access your portfolio
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>
                {error}
                <Button size="small" startIcon={<Refresh />} onClick={handleRetry} sx={{ mt: 1 }}>
                  Retry
                </Button>
              </Alert>
            )}

            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography sx={{ fontSize: 13, color: '#8b949e' }}>Signing in...</Typography>
              </Box>
            )}

            {/* Google Sign-In Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <div ref={googleButtonRef}></div>
            </Box>

            {!googleLoaded && !loading && !error && (
              <Button variant="outlined" startIcon={<Refresh />} onClick={handleRetry} fullWidth sx={{ mb: 2 }}>
                Load Sign-In
              </Button>
            )}

            {/* Divider */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2.5 }}>
              <Box sx={{ flex: 1, height: '1px', backgroundColor: '#21262d' }} />
              <Typography sx={{ fontSize: 12, color: '#484f58' }}>What you get</Typography>
              <Box sx={{ flex: 1, height: '1px', backgroundColor: '#21262d' }} />
            </Box>

            {/* Features */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
              {[
                { icon: '📊', text: '10 technical strategies with AI override' },
                { icon: '🤖', text: 'Real-time AI analysis on every stock' },
                { icon: '📰', text: 'News-driven signals with vector search' },
                { icon: '⚡', text: 'Automated trading with risk management' },
              ].map((item) => (
                <Box key={item.text} sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1.5,
                      backgroundColor: 'rgba(88,166,255,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontSize: 13, color: '#8b949e' }}>{item.text}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Stats */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 3, md: 5 }, mt: 5, flexWrap: 'wrap' }}>
            {[
              { number: '50+', label: 'Stocks Tracked' },
              { number: '10', label: 'Strategies' },
              { number: '24/7', label: 'News Monitoring' },
              { number: '3x', label: 'Daily Trading' },
            ].map((stat) => (
              <Box key={stat.label} sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.5px' }}>
                  {stat.number}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#484f58', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          position: 'relative',
          textAlign: 'center',
          py: 2.5,
          borderTop: '1px solid #21262d',
          fontSize: 12,
          color: '#484f58',
        }}
      >
        © 2026 Simple Analysis —{' '}
        <Box component="a" href="http://www.smplanalysis.com" sx={{ color: '#58a6ff', textDecoration: 'none' }}>
          smplanalysis.com
        </Box>
      </Box>
    </Box>
  );
};

export default LoginAlternative;
